import React from "react";
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Divider, FormControlLabel,
  Grid, MenuItem, Paper, Stack, TextField, Typography,
} from "@mui/material";
import AutorenewRoundedIcon from "@mui/icons-material/AutorenewRounded";
import CardMembershipRoundedIcon from "@mui/icons-material/CardMembershipRounded";
import PauseCircleOutlineRoundedIcon from "@mui/icons-material/PauseCircleOutlineRounded";
import PlayCircleOutlineRoundedIcon from "@mui/icons-material/PlayCircleOutlineRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded";
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import UndoRoundedIcon from "@mui/icons-material/UndoRounded";
import { Link as RouterLink } from "react-router-dom";
import { customersApi } from "../../api/customers";
import DashboardLoadingState from "./DashboardLoadingState";

const BRAND = { orange: "#F35A3A", dark: "#19242F" };
const PERIOD_LABELS = { days: "días", weeks: "semanas", months: "meses", years: "años" };
const PAYMENT_METHOD_LABELS = {
  cash: "Efectivo",
  bank_transfer: "Transferencia",
  card: "Tarjeta",
  digital_wallet: "Billetera virtual",
  check: "Cheque",
  other: "Otro",
};
const EMPTY_PAYMENT_CONTEXT = {
  mode: "administrative", systemActive: false, canRecord: false, canReadMovements: false,
  methods: [], registers: [], note: "",
};

function todayDate() {
  const now = new Date();
  const offset = now.getTimezoneOffset() * 60000;
  return new Date(now.getTime() - offset).toISOString().slice(0, 10);
}

function createOperationKey() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  return `membership-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

function formatDateOnly(value) {
  if (!value) return "—";
  const [year, month, day] = String(value).split("-");
  return year && month && day ? `${day}/${month}/${year}` : value;
}

function formatDateTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("es-AR", { dateStyle: "short", timeStyle: "short" });
}

function formatMoney(value, currency = "ARS") {
  return new Intl.NumberFormat("es-AR", {
    style: "currency", currency: currency || "ARS", maximumFractionDigits: 2,
  }).format(Number(value || 0));
}

function statusMeta(status) {
  if (status === "scheduled") return { label: "Programada", color: "info" };
  if (status === "paused") return { label: "Pausada", color: "warning" };
  if (status === "expired") return { label: "Vencida", color: "default" };
  if (status === "cancelled") return { label: "Cancelada", color: "error" };
  return { label: "Activa", color: "success" };
}

function paymentMeta(payment) {
  if (payment?.mode === "erp" || payment?.status === "accounted") {
    return { label: "Contabilizada en Gestión", color: "success" };
  }
  if ((payment?.mode === "administrative" || payment?.status === "administrative") && payment?.online?.provider === "mercadopago") {
    return { label: "Pagada online · Mercado Pago", color: "success" };
  }
  if (payment?.mode === "administrative" || payment?.status === "administrative") {
    return { label: "Cobrada administrativamente", color: "info" };
  }
  if (["pending", "in_process", "authorized"].includes(String(payment?.online?.providerStatus || "").toLowerCase())) {
    return { label: "Pago online en proceso", color: "warning" };
  }
  return { label: "Sin cobrar", color: "warning" };
}

function auditActorLabel(actor) {
  return actor?.name || actor?.email || "Responsable no registrado";
}

function auditEventLabel(type) {
  if (type === "renewed") return "Renovación";
  if (type === "paused") return "Pausa";
  if (type === "resumed") return "Reanudación";
  if (type === "cancelled") return "Cancelación";
  if (type === "expired") return "Vencimiento";
  if (type === "payment_recorded") return "Cobro";
  if (type === "usage_consumed") return "Uso registrado";
  if (type === "usage_reversed") return "Consumo anulado";
  return "Creación / asignación";
}

function auditEventDetails(event) {
  const details = event?.details || {};
  if (event?.type === "assigned" || event?.type === "renewed") {
    return [
      details.startDate && details.endDate ? `${formatDateOnly(details.startDate)} → ${formatDateOnly(details.endDate)}` : "",
      Number.isFinite(Number(details.amount)) ? formatMoney(details.amount, details.currency || "ARS") : "",
    ].filter(Boolean).join(" · ");
  }
  if (event?.type === "paused") {
    return [
      details.effectiveDate ? `desde ${formatDateOnly(details.effectiveDate)}` : "",
      details.reason || "",
    ].filter(Boolean).join(" · ");
  }
  if (event?.type === "resumed") {
    return [
      details.effectiveDate ? `desde ${formatDateOnly(details.effectiveDate)}` : "",
      Number.isFinite(Number(details.frozenDays)) ? `${Number(details.frozenDays)} días congelados` : "",
      details.note || "",
    ].filter(Boolean).join(" · ");
  }
  if (event?.type === "cancelled") return details.reason || "Sin motivo registrado";
  if (event?.type === "expired") return details.effectiveDate ? `venció ${formatDateOnly(details.effectiveDate)}` : "Período vencido";
  if (event?.type === "payment_recorded") {
    return [
      Number.isFinite(Number(details.amount)) ? formatMoney(details.amount, details.currency || "ARS") : "",
      PAYMENT_METHOD_LABELS[details.method] || details.method || "",
      details.cashRegisterName ? `Caja ${details.cashRegisterName}` : "",
      details.erpMovementNumber ? `Mov. ${details.erpMovementNumber}` : "",
      details.businessTransactionNumber ? `Trans. ${details.businessTransactionNumber}` : "",
      details.reference ? `Ref. ${details.reference}` : "",
    ].filter(Boolean).join(" · ");
  }
  if (event?.type === "usage_consumed" || event?.type === "usage_reversed") {
    return [
      Number(details.quantity || 0) > 0 ? `${Number(details.quantity)} ${details.usageLabel || "usos"}` : "",
      details.note || "",
    ].filter(Boolean).join(" · ");
  }
  return "";
}

function firstUsableRegister(paymentContext, currency = "ARS") {
  return (paymentContext?.registers || []).find(
    (register) => register.available && register.currency === currency,
  ) || null;
}

function emptyForm() {
  return {
    itemId: "", startDate: todayDate(), validityMode: "period", periodValue: 1, periodUnit: "months",
    endDate: "", value: "", currency: "ARS", renewFromMembershipId: "",
    operationKey: createOperationKey(), recordPayment: false, cashRegisterId: "",
    paymentMethod: "cash", paymentReference: "",
  };
}

function renewalStart(membership) {
  if (membership?.recommendedRenewalStart) return membership.recommendedRenewalStart;
  const today = todayDate();
  const operationalEndDate = membership?.effectiveEndDate || membership?.endDate;
  if (!operationalEndDate || operationalEndDate < today) return today;
  const date = new Date(`${operationalEndDate}T00:00:00.000Z`);
  date.setUTCDate(date.getUTCDate() + 1);
  return date.toISOString().slice(0, 10);
}

function addPeriodDate(startDate, value, unit) {
  if (!startDate) return "";
  const date = new Date(`${startDate}T00:00:00.000Z`);
  const amount = Number(value);
  if (Number.isNaN(date.getTime()) || !Number.isInteger(amount) || amount < 1) return "";
  if (unit === "days") date.setUTCDate(date.getUTCDate() + amount);
  if (unit === "weeks") date.setUTCDate(date.getUTCDate() + amount * 7);
  if (unit === "months" || unit === "years") {
    const originalDay = date.getUTCDate();
    date.setUTCDate(1);
    if (unit === "months") date.setUTCMonth(date.getUTCMonth() + amount);
    else date.setUTCFullYear(date.getUTCFullYear() + amount);
    const lastDay = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0)).getUTCDate();
    date.setUTCDate(Math.min(originalDay, lastDay));
  }
  return date.toISOString().slice(0, 10);
}

function daysBetween(fromDate, toDate) {
  const from = new Date(`${fromDate}T00:00:00.000Z`);
  const to = new Date(`${toDate}T00:00:00.000Z`);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return null;
  return Math.round((to.getTime() - from.getTime()) / 86400000);
}

function addDaysDate(dateValue, days) {
  if (!dateValue) return "";
  const date = new Date(`${dateValue}T00:00:00.000Z`);
  const amount = Number(days);
  if (Number.isNaN(date.getTime()) || !Number.isFinite(amount)) return "";
  date.setUTCDate(date.getUTCDate() + amount);
  return date.toISOString().slice(0, 10);
}

function membershipEndDate(membership) {
  return membership?.effectiveEndDate || membership?.endDate || "";
}

function latestResumeDate(membership) {
  return (membership?.pause?.history || [])
    .map((pause) => pause?.resumedDate || "")
    .filter(Boolean)
    .sort()
    .slice(-1)[0] || "";
}

function membershipOverview(memberships) {
  const today = todayDate();
  const rows = Array.isArray(memberships) ? memberships.filter((item) => item.status !== "cancelled") : [];
  const paused = rows.filter((item) => item.status === "paused").sort((a, b) => String(membershipEndDate(a)).localeCompare(String(membershipEndDate(b))));
  const active = rows.filter((item) => item.status === "active").sort((a, b) => String(membershipEndDate(a)).localeCompare(String(membershipEndDate(b))));
  const future = rows.filter((item) => item.status === "scheduled").sort((a, b) => String(a.startDate).localeCompare(String(b.startDate)));
  const expiring = active.filter((item) => {
    const operationalEndDate = membershipEndDate(item);
    const days = daysBetween(today, operationalEndDate);
    if (days === null || days < 0 || days > 7) return false;
    if (item.renewedToMembershipId) return false;
    const continuation = new Date(`${operationalEndDate}T00:00:00.000Z`);
    continuation.setUTCDate(continuation.getUTCDate() + 1);
    const continuationLimit = continuation.toISOString().slice(0, 10);
    return !future.some((next) => next.renewedFromMembershipId === item.id && String(next.startDate) <= continuationLimit);
  });
  const expired = rows.filter((item) => item.status === "expired").sort((a, b) => String(membershipEndDate(b)).localeCompare(String(membershipEndDate(a))));

  if (paused.length) return { status: "paused", membership: paused[0], attention: false };
  if (expiring.length) return { status: "expiring", membership: expiring[0], attention: true };
  if (active.length) return { status: "active", membership: active[0], attention: false };
  if (future.length) return { status: "future", membership: future[0], attention: false };
  if (expired.length) return { status: "expired", membership: expired[0], attention: true };
  return { status: "none", membership: null, attention: false };
}

function membershipSuggestion(item) {
  if (!item || item.membershipEligible !== true) return null;
  const plan = item.membershipPlan || {};
  const periodValue = Number(plan.periodValue);
  const periodUnit = ["days", "weeks", "months", "years"].includes(plan.periodUnit)
    ? plan.periodUnit
    : "months";
  const referencePrice = Number(plan.referencePrice);
  const includedUses = Number(plan.includedUses);
  const usageEnabled = plan.usageEnabled === true && Number.isInteger(includedUses) && includedUses > 0;
  return {
    periodValue: Number.isInteger(periodValue) && periodValue > 0 ? periodValue : 1,
    periodUnit,
    value: Number.isFinite(referencePrice) && referencePrice >= 0
      ? referencePrice
      : Number(item.price || 0),
    usageEnabled,
    includedUses: usageEnabled ? includedUses : null,
    usageLabel: String(plan.usageLabel || "usos").trim() || "usos",
  };
}

function renewalForm(membership, paymentContext = EMPTY_PAYMENT_CONTEXT, item = null) {
  const currency = membership?.currency || "ARS";
  const canRecordPayment = paymentContext.canRecord === true;
  const register = paymentContext.mode === "erp" ? firstUsableRegister(paymentContext, currency) : null;
  const suggestion = membershipSuggestion(item);
  return {
    itemId: membership?.item?.id || "",
    startDate: renewalStart(membership),
    validityMode: "period",
    periodValue: suggestion?.periodValue ?? (membership?.period?.value > 0 ? membership.period.value : 1),
    periodUnit: suggestion?.periodUnit ?? (membership?.period?.unit === "custom" ? "months" : membership?.period?.unit || "months"),
    endDate: "", value: String(suggestion?.value ?? membership?.value ?? 0),
    currency, renewFromMembershipId: membership?.id || "",
    operationKey: createOperationKey(), recordPayment: canRecordPayment,
    cashRegisterId: register?.id || "", paymentMethod: "cash", paymentReference: "",
  };
}

function paymentForm(membership, paymentContext = EMPTY_PAYMENT_CONTEXT) {
  const currency = membership?.currency || "ARS";
  const register = paymentContext.mode === "erp" ? firstUsableRegister(paymentContext, currency) : null;
  return {
    membershipId: membership?.id || "",
    cashRegisterId: register?.id || "",
    paymentMethod: "cash",
    paymentReference: "",
  };
}

export default function CustomerMembershipsCard({ projectId, customerId, enabled, canManage, canCollect = false, renewMembershipId = "", collectMembershipId = "", onMembershipChanged }) {
  const [items, setItems] = React.useState([]);
  const [memberships, setMemberships] = React.useState([]);
  const [paymentConfig, setPaymentConfig] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [cancelling, setCancelling] = React.useState(false);
  const [cancelMembershipId, setCancelMembershipId] = React.useState("");
  const [cancelReason, setCancelReason] = React.useState("");
  const [error, setError] = React.useState("");
  const [message, setMessage] = React.useState("");
  const [form, setForm] = React.useState(emptyForm);
  const [pauseAction, setPauseAction] = React.useState(null);
  const [pauseSaving, setPauseSaving] = React.useState(false);
  const [paymentAction, setPaymentAction] = React.useState(null);
  const [paymentSaving, setPaymentSaving] = React.useState(false);
  const [usageAction, setUsageAction] = React.useState(null);
  const [usageSaving, setUsageSaving] = React.useState(false);
  const autoRenewedRef = React.useRef("");
  const autoCollectedRef = React.useRef("");
  const submittingRef = React.useRef(false);
  const pauseSubmittingRef = React.useRef(false);
  const paymentSubmittingRef = React.useRef(false);
  const usageSubmittingRef = React.useRef(false);

  const load = React.useCallback(async () => {
    if (!enabled || !projectId || !customerId) return;
    setLoading(true); setError("");
    try {
      const [itemsData, membershipData] = await Promise.all([
        customersApi.membershipItems(projectId),
        customersApi.memberships(projectId, customerId),
      ]);
      setItems(itemsData.items || []);
      setPaymentConfig(itemsData.payment || EMPTY_PAYMENT_CONTEXT);
      setMemberships(membershipData.memberships || []);
    } catch (err) {
      setError(err.message || "No se pudieron cargar las membresías.");
    } finally {
      setLoading(false);
    }
  }, [customerId, enabled, projectId]);

  React.useEffect(() => {
    setForm(emptyForm()); setItems([]); setMemberships([]); setPaymentConfig(null); setMessage(""); setError("");
    setPauseAction(null); setPauseSaving(false);
    setPaymentAction(null); setPaymentSaving(false);
    setUsageAction(null); setUsageSaving(false);
    setCancelMembershipId(""); setCancelReason(""); setCancelling(false);
    autoRenewedRef.current = "";
    autoCollectedRef.current = "";
    submittingRef.current = false;
    pauseSubmittingRef.current = false;
    paymentSubmittingRef.current = false;
    usageSubmittingRef.current = false;
    load();
  }, [load]);

  React.useEffect(() => {
    if (!canManage || !paymentConfig || !renewMembershipId || !memberships.length || autoRenewedRef.current === renewMembershipId) return;
    const target = memberships.find((membership) => membership.id === renewMembershipId);
    if (!target) return;
    autoRenewedRef.current = renewMembershipId;
    if (target.status === "paused") {
      setForm(emptyForm());
      setMessage("");
      setError("La membresía está pausada. Reanudala primero para cerrar el tiempo congelado y calcular correctamente la renovación.");
      return;
    }
    if (target.renewedToMembershipId) {
      setForm(emptyForm());
      setMessage("");
      setError("Este período ya tiene una renovación en el historial. Revisá el período siguiente antes de volver a renovar.");
      return;
    }
    const item = items.find((candidate) => candidate.id === target.item?.id) || null;
    setForm(renewalForm(target, paymentConfig, item));
    setMessage("");
    setError("");
  }, [canManage, items, memberships, paymentConfig, renewMembershipId]);

  React.useEffect(() => {
    if (!canCollect || !paymentConfig || !collectMembershipId || !memberships.length || autoCollectedRef.current === collectMembershipId) return;
    const target = memberships.find((membership) => membership.id === collectMembershipId);
    if (!target) return;
    autoCollectedRef.current = collectMembershipId;
    setForm(emptyForm());
    setPauseAction(null);
    setCancelMembershipId("");
    setCancelReason("");
    setMessage("");
    if (target.payment?.recorded) {
      setPaymentAction(null);
      setError("Esta membresía ya tiene un cobro registrado. No se puede cobrar nuevamente el mismo saldo.");
      return;
    }
    if (!(Number(target.value) > 0)) {
      setPaymentAction(null);
      setError("Esta membresía no tiene un saldo mayor a cero para cobrar.");
      return;
    }
    if (!paymentConfig.canRecord) {
      setPaymentAction(null);
      setError(paymentConfig.note || "Tus permisos actuales no permiten registrar este cobro.");
      return;
    }
    setPaymentAction(paymentForm(target, paymentConfig));
    setError("");
  }, [canCollect, collectMembershipId, memberships, paymentConfig]);

  if (!enabled) return null;

  const resolvedPaymentConfig = paymentConfig || EMPTY_PAYMENT_CONTEXT;
  const selectedItem = items.find((candidate) => candidate.id === form.itemId) || null;
  const selectedSuggestion = membershipSuggestion(selectedItem);
  const selectedRegister = (resolvedPaymentConfig.registers || []).find((register) => register.id === form.cashRegisterId) || null;
  const paymentReady = !form.recordPayment
    || (resolvedPaymentConfig.mode === "administrative" && resolvedPaymentConfig.canRecord)
    || (
      resolvedPaymentConfig.mode === "erp"
      && resolvedPaymentConfig.canRecord
      && selectedRegister?.available
      && selectedRegister.currency === form.currency
    );

  const change = (field) => (event) => setForm((current) => ({ ...current, [field]: event.target.value }));

  const selectItem = (event) => {
    const itemId = event.target.value;
    const item = items.find((candidate) => candidate.id === itemId);
    const suggestion = membershipSuggestion(item);
    setForm((current) => ({
      ...current,
      itemId,
      ...(suggestion
        ? {
            validityMode: "period",
            periodValue: suggestion.periodValue,
            periodUnit: suggestion.periodUnit,
            endDate: "",
            value: String(suggestion.value),
          }
        : { value: current.renewFromMembershipId ? current.value : String(item?.price ?? 0) }),
    }));
  };

  const beginRenewal = (membership) => {
    setMessage(""); setError(""); setPauseAction(null); setPaymentAction(null); setUsageAction(null);
    if (membership?.status === "paused") {
      setError("La membresía está pausada. Reanudala primero para cerrar los días congelados y calcular el próximo inicio.");
      return;
    }
    if (membership?.renewedToMembershipId) {
      setError("Este período ya fue renovado. Continuá desde el último período de la cadena para evitar una superposición.");
      return;
    }
    const item = items.find((candidate) => candidate.id === membership.item?.id) || null;
    setForm(renewalForm(membership, resolvedPaymentConfig, item));
  };

  const beginPause = (membership) => {
    setMessage(""); setError(""); setPaymentAction(null); setUsageAction(null);
    if (membership?.status !== "active") {
      setError("Sólo se puede pausar una membresía vigente. La pausa no reemplaza una cancelación, una mora ni la suspensión del cliente.");
      return;
    }
    if (membership?.renewedToMembershipId) {
      setError("Este período ya tiene una renovación programada. Revisá la cadena antes de congelar para no superponer vigencias.");
      return;
    }
    setForm(emptyForm());
    const latestResume = latestResumeDate(membership);
    setPauseAction({
      type: "pause",
      membershipId: membership.id,
      startDate: latestResume && latestResume > todayDate() ? latestResume : todayDate(),
      reason: "",
      note: "",
    });
  };

  const beginResume = (membership) => {
    const currentPause = membership?.pause?.current;
    setMessage(""); setError(""); setPaymentAction(null); setUsageAction(null);
    if (!currentPause) {
      setError("La membresía ya no tiene una pausa abierta. Actualizá la ficha para ver su estado actual.");
      return;
    }
    setForm(emptyForm());
    setPauseAction({
      type: "resume",
      membershipId: membership.id,
      resumeDate: todayDate(),
      reason: "",
      note: "",
    });
  };

  const changePauseAction = (field) => (event) => setPauseAction((current) => (current ? { ...current, [field]: event.target.value } : current));

  const submitPauseAction = async (event) => {
    event.preventDefault();
    if (!canManage || !pauseAction?.membershipId || pauseSaving || pauseSubmittingRef.current) return;
    const target = memberships.find((membership) => membership.id === pauseAction.membershipId);
    if (!target) return;
    if (pauseAction.type === "pause" && String(pauseAction.reason || "").trim().length < 3) {
      setError("Indicá un motivo concreto para dejar trazabilidad de la pausa.");
      return;
    }

    pauseSubmittingRef.current = true;
    setPauseSaving(true); setError(""); setMessage("");
    try {
      const data = pauseAction.type === "resume"
        ? await customersApi.updateMembershipStatus(projectId, customerId, target.id, {
            action: "resume",
            resumeDate: pauseAction.resumeDate || undefined,
            note: pauseAction.note || undefined,
          })
        : await customersApi.updateMembershipStatus(projectId, customerId, target.id, {
            action: "pause",
            startDate: pauseAction.startDate || undefined,
            reason: String(pauseAction.reason || "").trim(),
          });
      setMessage(data.message || (pauseAction.type === "resume" ? "Membresía reanudada." : "Membresía pausada."));
      setPauseAction(null);
      await load();
      if (onMembershipChanged) await onMembershipChanged();
    } catch (err) {
      setError(err.message || "No se pudo actualizar la pausa de la membresía.");
      if (["MEMBERSHIP_ALREADY_PAUSED", "MEMBERSHIP_NOT_PAUSED", "MEMBERSHIP_CHANGED", "MEMBERSHIP_PAUSE_HAS_CONTINUATION"].includes(err?.code)) {
        setPauseAction(null);
        await load();
        if (onMembershipChanged) await onMembershipChanged();
      }
    } finally {
      pauseSubmittingRef.current = false;
      setPauseSaving(false);
    }
  };

  const beginCancellation = (membership) => {
    setMessage(""); setError(""); setPaymentAction(null); setUsageAction(null);
    setCancelMembershipId(membership?.id || "");
    setCancelReason("");
  };

  const cancelMembership = async (membership) => {
    const reason = cancelReason.trim();
    if (!canManage || !membership?.id || !reason || cancelling) return;
    setCancelling(true); setError(""); setMessage("");
    try {
      const data = await customersApi.updateMembershipStatus(projectId, customerId, membership.id, { action: "cancel", reason });
      setMessage(data.message || "Membresía cancelada.");
      setCancelMembershipId(""); setCancelReason("");
      await load();
      if (onMembershipChanged) await onMembershipChanged();
    } catch (err) {
      setError(err.message || "No se pudo cancelar la membresía.");
    } finally {
      setCancelling(false);
    }
  };

  const beginPayment = (membership) => {
    setMessage(""); setError(""); setPauseAction(null); setUsageAction(null);
    if (!canCollect) {
      setPaymentAction(null);
      setError("Tu acceso no permite registrar cobros de membresías.");
      return;
    }
    setCancelMembershipId(""); setCancelReason("");
    if (membership?.payment?.recorded) {
      setPaymentAction(null);
      setError("Esta membresía ya tiene un cobro registrado. No se puede cobrar nuevamente el mismo saldo.");
      return;
    }
    if (!(Number(membership?.value) > 0)) {
      setPaymentAction(null);
      setError("Esta membresía no tiene un saldo mayor a cero para cobrar.");
      return;
    }
    if (!resolvedPaymentConfig.canRecord) {
      setPaymentAction(null);
      setError(resolvedPaymentConfig.note || "Tus permisos actuales no permiten registrar este cobro.");
      return;
    }
    setForm(emptyForm());
    setPaymentAction(paymentForm(membership, resolvedPaymentConfig));
  };

  const changePaymentAction = (field) => (event) => setPaymentAction((current) => (
    current ? { ...current, [field]: event.target.value } : current
  ));

  const submitPayment = async (event) => {
    event.preventDefault();
    if (!canCollect || !paymentAction?.membershipId || paymentSaving || paymentSubmittingRef.current) return;
    const target = memberships.find((membership) => membership.id === paymentAction.membershipId);
    if (!target || target.payment?.recorded) {
      setPaymentAction(null);
      setError("La membresía ya no tiene un saldo pendiente. Actualizamos el historial para evitar un cobro duplicado.");
      await load();
      if (onMembershipChanged) await onMembershipChanged();
      return;
    }

    const register = (resolvedPaymentConfig.registers || []).find((candidate) => candidate.id === paymentAction.cashRegisterId) || null;
    const ready = resolvedPaymentConfig.mode === "administrative"
      ? resolvedPaymentConfig.canRecord
      : resolvedPaymentConfig.canRecord && register?.available && register.currency === target.currency;
    if (!ready) {
      setError("Elegí una caja o cuenta disponible para la moneda de esta membresía.");
      return;
    }

    paymentSubmittingRef.current = true;
    setPaymentSaving(true); setError(""); setMessage("");
    try {
      const data = await customersApi.recordMembershipPayment(projectId, customerId, target.id, {
        paymentMode: resolvedPaymentConfig.mode,
        cashRegisterId: resolvedPaymentConfig.mode === "erp" ? paymentAction.cashRegisterId || undefined : undefined,
        paymentMethod: resolvedPaymentConfig.mode === "administrative" ? paymentAction.paymentMethod : undefined,
        paymentReference: paymentAction.paymentReference || undefined,
      });
      setMessage(data.message || "Cobro registrado sobre la membresía.");
      setPaymentAction(null);
      await load();
      if (onMembershipChanged) await onMembershipChanged();
    } catch (err) {
      if (["MEMBERSHIP_CHANGED", "MEMBERSHIP_PAYMENT_OPERATION_CONFLICT"].includes(err?.code)) {
        setPaymentAction(null);
        setError(err.message || "La membresía cambió mientras se registraba el cobro. Actualizamos el historial.");
        await load();
        if (onMembershipChanged) await onMembershipChanged();
      } else {
        setError(err.message || "No se pudo registrar el cobro de la membresía.");
      }
    } finally {
      paymentSubmittingRef.current = false;
      setPaymentSaving(false);
    }
  };

  const beginUsage = (membership) => {
    setMessage(""); setError(""); setPauseAction(null); setPaymentAction(null);
    setCancelMembershipId(""); setCancelReason("");
    if (!canManage || membership?.status !== "active" || !membership?.usage?.controlled) return;
    if (membership.usage.exhausted) {
      setError("Cupo agotado para este período.");
      return;
    }
    setUsageAction({
      type: "consume",
      membershipId: membership.id,
      quantity: "1",
      note: "",
      operationKey: createOperationKey(),
    });
    setForm(emptyForm());
  };

  const beginUsageReversal = (membership, movement) => {
    if (!canManage || !movement?.id) return;
    setMessage(""); setError(""); setPauseAction(null); setPaymentAction(null);
    setCancelMembershipId(""); setCancelReason("");
    setUsageAction({
      type: "reverse",
      membershipId: membership.id,
      movementId: movement.id,
      quantity: String(movement.quantity || 1),
      note: "",
      operationKey: createOperationKey(),
    });
    setForm(emptyForm());
  };

  const submitUsage = async (event) => {
    event.preventDefault();
    if (!canManage || !usageAction?.membershipId || usageSaving || usageSubmittingRef.current) return;
    const membership = memberships.find((row) => row.id === usageAction.membershipId);
    if (!membership) return;
    if (usageAction.type === "consume") {
      const quantity = Number(usageAction.quantity);
      if (!Number.isInteger(quantity) || quantity < 1) {
        setError("La cantidad a consumir debe ser un número entero positivo.");
        return;
      }
      if (quantity > Number(membership.usage?.available || 0)) {
        setError("La cantidad supera el cupo disponible de este período.");
        return;
      }
    }

    usageSubmittingRef.current = true;
    setUsageSaving(true); setError(""); setMessage("");
    try {
      const data = await customersApi.recordMembershipUsage(projectId, customerId, membership.id, {
        action: usageAction.type,
        quantity: usageAction.type === "consume" ? Number(usageAction.quantity) : undefined,
        movementId: usageAction.type === "reverse" ? usageAction.movementId : undefined,
        note: String(usageAction.note || "").trim() || undefined,
        operationKey: usageAction.operationKey,
      });
      setMessage(data.message || (usageAction.type === "reverse" ? "Consumo anulado." : "Uso registrado."));
      setUsageAction(null);
      await load();
      if (onMembershipChanged) await onMembershipChanged();
    } catch (err) {
      setError(err.message || "No se pudo actualizar el consumo de la membresía.");
      if (["MEMBERSHIP_CHANGED", "MEMBERSHIP_USAGE_LIMIT_EXCEEDED", "MEMBERSHIP_USAGE_STATUS_INVALID"].includes(err?.code)) {
        await load();
        if (onMembershipChanged) await onMembershipChanged();
      }
    } finally {
      usageSubmittingRef.current = false;
      setUsageSaving(false);
    }
  };

  const togglePayment = (event) => {
    const checked = event.target.checked;
    setForm((current) => {
      if (!checked) return { ...current, recordPayment: false };
      if (resolvedPaymentConfig.mode !== "erp") return { ...current, recordPayment: true };
      const register = firstUsableRegister(resolvedPaymentConfig, current.currency);
      return { ...current, recordPayment: true, cashRegisterId: current.cashRegisterId || register?.id || "" };
    });
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!canManage || !form.itemId || !form.startDate || saving || submittingRef.current || !paymentReady) return;
    submittingRef.current = true;
    setSaving(true); setError(""); setMessage("");
    try {
      const data = await customersApi.assignMembership(projectId, customerId, {
        itemId: form.itemId,
        startDate: form.startDate,
        endDate: form.validityMode === "endDate" ? form.endDate || undefined : undefined,
        periodValue: form.validityMode === "period" ? Number(form.periodValue || 0) : undefined,
        periodUnit: form.validityMode === "period" ? form.periodUnit : undefined,
        value: form.value === "" ? undefined : Number(form.value),
        currency: form.currency || "ARS",
        renewFromMembershipId: form.renewFromMembershipId || undefined,
        operationKey: form.operationKey,
        recordPayment: form.recordPayment,
        paymentMode: form.recordPayment ? resolvedPaymentConfig.mode : undefined,
        cashRegisterId: form.recordPayment && resolvedPaymentConfig.mode === "erp" ? form.cashRegisterId || undefined : undefined,
        paymentMethod: form.recordPayment && resolvedPaymentConfig.mode === "administrative" ? form.paymentMethod : undefined,
        paymentReference: form.recordPayment ? form.paymentReference || undefined : undefined,
      });
      setMessage(data.message || "Membresía guardada.");
      setForm(emptyForm());
      await load();
      if (onMembershipChanged) await onMembershipChanged();
    } catch (err) {
      if (err?.code === "MEMBERSHIP_RENEWAL_START_CONFLICT" && err?.data?.recommendedRenewalStart) {
        setForm((current) => ({ ...current, startDate: err.data.recommendedRenewalStart }));
        setError(`${err.message || "La fecha de inicio se superpone con la vigencia actual."} Ajustamos el inicio sugerido para que puedas revisar y confirmar.`);
      } else if (["MEMBERSHIP_RENEWAL_ALREADY_CONTINUED", "MEMBERSHIP_RENEWAL_OVERLAP"].includes(err?.code)) {
        setForm(emptyForm());
        setError(err.message || "La cadena ya cambió. Actualizamos el historial para evitar una renovación superpuesta.");
        await load();
        if (onMembershipChanged) await onMembershipChanged();
      } else {
        setError(err.message || "No se pudo guardar la membresía.");
      }
    } finally {
      submittingRef.current = false;
      setSaving(false);
    }
  };

  const overview = membershipOverview(memberships);
  const overviewMembership = overview.membership;
  const overviewLabels = {
    active: "Vigente", paused: "Pausada", expiring: "Próxima a vencer", expired: "Vencida", future: "Futura", none: "Sin membresía",
  };
  const availableRegisters = (resolvedPaymentConfig.registers || []).filter(
    (register) => register.available && register.currency === form.currency,
  );
  const renewalSource = form.renewFromMembershipId
    ? memberships.find((membership) => membership.id === form.renewFromMembershipId) || null
    : null;
  const renewalMinimumStart = renewalSource?.recommendedRenewalStart || (renewalSource ? renewalStart(renewalSource) : "");
  const changingPlan = Boolean(renewalSource && selectedItem && selectedItem.id !== renewalSource.item?.id);
  const newAssignmentItems = items.filter((item) => item.eligibleForNewAssignment !== false && item.membershipEligible === true);
  const selectableItems = form.renewFromMembershipId
    ? items.filter((item) => item.eligibleForNewAssignment !== false || item.id === renewalSource?.item?.id)
    : newAssignmentItems;
  const previewEndDate = form.validityMode === "endDate"
    ? form.endDate
    : addPeriodDate(form.startDate, Number(form.periodValue), form.periodUnit);
  const previewAmount = Number(form.value);
  const previewReady = Boolean(
    selectedItem
    && form.startDate
    && previewEndDate
    && previewEndDate >= form.startDate
    && (!renewalMinimumStart || form.startDate >= renewalMinimumStart)
    && form.value !== ""
    && Number.isFinite(previewAmount)
    && previewAmount >= 0
    && (!form.recordPayment || previewAmount > 0)
    && paymentReady,
  );
  const previewValidity = form.validityMode === "endDate"
    ? `hasta ${formatDateOnly(previewEndDate)}`
    : `${form.periodValue || 0} ${PERIOD_LABELS[form.periodUnit] || form.periodUnit} · vence ${formatDateOnly(previewEndDate)}`;
  const previewAmountLabel = form.value !== "" && Number.isFinite(previewAmount)
    ? formatMoney(previewAmount, form.currency)
    : "Importe pendiente";
  const previewUsage = selectedSuggestion?.usageEnabled
    ? `Incluye ${selectedSuggestion.includedUses} ${selectedSuggestion.usageLabel} por período`
    : renewalSource && selectedItem?.id === renewalSource.item?.id && renewalSource.usage?.controlled
      ? `Incluye ${renewalSource.usage.included} ${renewalSource.usage.label || "usos"} por período`
      : "Sin control de usos";
  const previewPayment = !form.recordPayment
    ? "Sin cobro ahora"
    : resolvedPaymentConfig.mode === "erp"
      ? selectedRegister
        ? `Cobro en ${selectedRegister.name} · ${PAYMENT_METHOD_LABELS[selectedRegister.method] || selectedRegister.method}`
        : "Cobro pendiente de caja/cuenta"
      : `Cobro administrativo · ${PAYMENT_METHOD_LABELS[form.paymentMethod] || form.paymentMethod}`;

  const pauseTarget = pauseAction?.membershipId
    ? memberships.find((membership) => membership.id === pauseAction.membershipId) || null
    : null;
  const currentPause = pauseTarget?.pause?.current || null;
  const completedFrozenDays = (pauseTarget?.pause?.history || [])
    .filter((pause) => pause?.resumedDate)
    .reduce((sum, pause) => sum + Number(pause?.frozenDays || 0), 0);
  const selectedResumeFrozenDays = pauseAction?.type === "resume" && currentPause
    ? Math.max(0, daysBetween(currentPause.startDate, pauseAction.resumeDate) || 0)
    : 0;
  const projectedResumeEndDate = pauseTarget?.endDate
    ? addDaysDate(pauseTarget.endDate, completedFrozenDays + selectedResumeFrozenDays)
    : "";
  const pauseMinimumDate = pauseTarget
    ? [pauseTarget.startDate, latestResumeDate(pauseTarget)].filter(Boolean).sort().slice(-1)[0] || pauseTarget.startDate
    : "";
  const pauseActionDate = pauseAction?.type === "resume" ? pauseAction?.resumeDate : pauseAction?.startDate;
  const pauseActionReady = Boolean(
    pauseTarget
    && pauseActionDate
    && pauseActionDate <= todayDate()
    && (pauseAction?.type === "resume"
      ? currentPause && pauseActionDate >= currentPause.startDate
      : pauseActionDate >= pauseMinimumDate && String(pauseAction?.reason || "").trim().length >= 3),
  );
  const paymentTarget = paymentAction?.membershipId
    ? memberships.find((membership) => membership.id === paymentAction.membershipId) || null
    : null;
  const paymentSelectedRegister = paymentAction?.cashRegisterId
    ? (resolvedPaymentConfig.registers || []).find((register) => register.id === paymentAction.cashRegisterId) || null
    : null;
  const paymentAvailableRegisters = paymentTarget
    ? (resolvedPaymentConfig.registers || []).filter((register) => register.available && register.currency === paymentTarget.currency)
    : [];
  const paymentActionReady = Boolean(
    paymentTarget
    && !paymentTarget.payment?.recorded
    && Number(paymentTarget.value) > 0
    && resolvedPaymentConfig.canRecord
    && (
      resolvedPaymentConfig.mode === "administrative"
      || (
        resolvedPaymentConfig.mode === "erp"
        && paymentSelectedRegister?.available
        && paymentSelectedRegister.currency === paymentTarget.currency
      )
    ),
  );

  const usageTarget = usageAction?.membershipId
    ? memberships.find((membership) => membership.id === usageAction.membershipId) || null
    : null;
  const usageQuantity = Number(usageAction?.quantity || 0);
  const usageActionReady = Boolean(
    usageTarget
    && (usageAction?.type === "reverse"
      ? usageAction.movementId
      : Number.isInteger(usageQuantity) && usageQuantity > 0 && usageQuantity <= Number(usageTarget.usage?.available || 0)),
  );

  return (
    <Paper variant="outlined" sx={{ p: 2, borderColor: "rgba(25,36,47,.09)" }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.4 }}>
        <CardMembershipRoundedIcon sx={{ color: BRAND.orange }} />
        <Box>
          <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>Membresías</Typography>
          <Typography variant="body2" sx={{ color: "rgba(25,36,47,.55)" }}>
            Asigná, renová o pausá períodos sobre ítems reales. El período contratado y los cobros históricos se conservan.
          </Typography>
        </Box>
      </Stack>

      {!loading && overviewMembership ? (
        overview.attention ? (
          <Alert severity={overview.status === "expired" ? "error" : "warning"} sx={{ mb: 1.2 }}
            action={canManage ? <Button size="small" onClick={() => beginRenewal(overviewMembership)}>Renovar</Button> : null}>
            <strong>{overviewLabels[overview.status]}.</strong>{" "}
            {overview.status === "expired" ? "Venció" : "Vence"} el {formatDateOnly(membershipEndDate(overviewMembership))} · {overviewMembership.item?.name || "Membresía"}.
          </Alert>
        ) : (
          <Box sx={{ mb: 1.2, p: 1.2, borderRadius: 2, bgcolor: "rgba(25,36,47,.035)" }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="space-between" alignItems={{ sm: "center" }}>
              <Box>
                <Typography variant="caption" sx={{ color: "rgba(25,36,47,.5)", fontWeight: 850 }}>Estado de membresía</Typography>
                <Typography sx={{ fontWeight: 900, color: BRAND.dark }}>
                  {overviewLabels[overview.status]} · {overviewMembership.item?.name || "Membresía"}
                </Typography>
                <Typography variant="body2" sx={{ color: "rgba(25,36,47,.58)" }}>
                  {overview.status === "future"
                    ? `Comienza ${formatDateOnly(overviewMembership.startDate)}`
                    : overview.status === "paused"
                      ? `Pausada desde ${formatDateOnly(overviewMembership.pause?.current?.startDate)} · vencimiento operativo ${formatDateOnly(membershipEndDate(overviewMembership))}`
                      : `Vence ${formatDateOnly(membershipEndDate(overviewMembership))}`}
                </Typography>
                {overview.status === "paused" && overviewMembership.pause?.current?.reason ? (
                  <Typography variant="caption" sx={{ color: "rgba(25,36,47,.5)" }}>Motivo: {overviewMembership.pause.current.reason}</Typography>
                ) : null}
              </Box>
              <Stack direction="row" spacing={.7} flexWrap="wrap" useFlexGap alignItems="center">
                <Chip size="small" color={overview.status === "paused" ? "warning" : "default"} variant="outlined" label={overviewLabels[overview.status]} sx={{ fontWeight: 800 }} />
                <Chip
                  size="small"
                  color={paymentMeta(overviewMembership.payment).color}
                  variant="outlined"
                  label={paymentMeta(overviewMembership.payment).label}
                  sx={{ fontWeight: 800 }}
                />
                {canManage && overview.status === "paused" ? (
                  <Button size="small" variant="outlined" startIcon={<PlayCircleOutlineRoundedIcon />} onClick={() => beginResume(overviewMembership)}>Reanudar</Button>
                ) : null}
              </Stack>
            </Stack>
          </Box>
        )
      ) : null}

      {error ? <Alert severity="error" sx={{ mb: 1.2 }}>{error}</Alert> : null}
      {message ? <Alert severity="success" sx={{ mb: 1.2 }}>{message}</Alert> : null}

      {canManage && pauseAction && pauseTarget ? (
        <Box component="form" onSubmit={submitPauseAction} sx={{ mb: 1.4, p: 1.5, borderRadius: 2.5, border: "1px solid rgba(243,90,58,.2)", bgcolor: "rgba(243,90,58,.035)" }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="space-between" alignItems={{ sm: "center" }} sx={{ mb: 1 }}>
            <Box>
              <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>
                {pauseAction.type === "resume" ? "Reanudar membresía" : "Pausar membresía"} · {pauseTarget.item?.name || "Plan"}
              </Typography>
              <Typography variant="caption" sx={{ color: "rgba(25,36,47,.55)" }}>
                El vencimiento original {formatDateOnly(pauseTarget.endDate)} queda guardado sin cambios. Esta acción tampoco altera el cobro ni el estado de la cuenta del cliente.
              </Typography>
            </Box>
            <Button size="small" onClick={() => setPauseAction(null)} disabled={pauseSaving}>Cancelar</Button>
          </Stack>

          {pauseAction.type === "resume" ? (
            <Grid container spacing={1.2}>
              <Grid item xs={12} sm={5}>
                <TextField fullWidth required size="small" type="date" label="Fecha de reanudación" value={pauseAction.resumeDate || ""}
                  onChange={changePauseAction("resumeDate")} disabled={pauseSaving} InputLabelProps={{ shrink: true }}
                  inputProps={{ min: currentPause?.startDate || undefined, max: todayDate() }}
                  helperText={`Pausa iniciada ${formatDateOnly(currentPause?.startDate)}.`} />
              </Grid>
              <Grid item xs={12} sm={7}>
                <TextField fullWidth size="small" label="Nota de reanudación (opcional)" value={pauseAction.note || ""}
                  onChange={changePauseAction("note")} disabled={pauseSaving} inputProps={{ maxLength: 500 }} />
              </Grid>
              <Grid item xs={12}>
                <Alert severity="info">
                  Se cerrarán {selectedResumeFrozenDays} días congelados y el vencimiento operativo quedará en <strong>{formatDateOnly(projectedResumeEndDate)}</strong>.
                  {currentPause?.reason ? ` Motivo registrado: ${currentPause.reason}.` : ""}
                </Alert>
              </Grid>
            </Grid>
          ) : (
            <Grid container spacing={1.2}>
              <Grid item xs={12} sm={5}>
                <TextField fullWidth required size="small" type="date" label="Inicio de la pausa" value={pauseAction.startDate || ""}
                  onChange={changePauseAction("startDate")} disabled={pauseSaving} InputLabelProps={{ shrink: true }}
                  inputProps={{ min: pauseMinimumDate || undefined, max: todayDate() }}
                  helperText={`Dentro de la vigencia operativa. Hoy vence ${formatDateOnly(membershipEndDate(pauseTarget))}.`} />
              </Grid>
              <Grid item xs={12} sm={7}>
                <TextField fullWidth required size="small" label="Motivo" value={pauseAction.reason || ""}
                  onChange={changePauseAction("reason")} disabled={pauseSaving} inputProps={{ maxLength: 500 }}
                  helperText="Queda guardado con fecha y usuario para trazabilidad." />
              </Grid>
              <Grid item xs={12}>
                <Alert severity="warning">
                  Mientras esté pausada no se considera una membresía activa. Al reanudar, cada día congelado se suma al vencimiento operativo sin sobrescribir el período original.
                </Alert>
              </Grid>
            </Grid>
          )}

          <Button type="submit" variant="contained" sx={{ mt: 1.2 }} disabled={pauseSaving || !pauseActionReady}
            startIcon={pauseSaving ? <CircularProgress size={17} /> : pauseAction.type === "resume" ? <PlayCircleOutlineRoundedIcon /> : <PauseCircleOutlineRoundedIcon />}>
            {pauseSaving ? "Guardando…" : pauseAction.type === "resume" ? "Confirmar reanudación" : "Confirmar pausa"}
          </Button>
        </Box>
      ) : null}

      {canCollect && paymentAction && paymentTarget ? (
        <Box component="form" onSubmit={submitPayment} sx={{ mb: 1.4, p: 1.5, borderRadius: 2.5, border: "1px solid rgba(243,90,58,.2)", bgcolor: "rgba(243,90,58,.035)" }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="space-between" alignItems={{ sm: "center" }} sx={{ mb: 1 }}>
            <Box>
              <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>
                Registrar cobro pendiente · {paymentTarget.item?.name || "Membresía"}
              </Typography>
              <Typography variant="caption" sx={{ color: "rgba(25,36,47,.55)" }}>
                Se cobra el saldo de este período existente. No se renueva la membresía ni se crea una nueva vigencia.
              </Typography>
            </Box>
            <Button size="small" onClick={() => setPaymentAction(null)} disabled={paymentSaving}>Cancelar</Button>
          </Stack>

          <Alert severity="info" sx={{ mb: 1.2 }}>
            {formatDateOnly(paymentTarget.startDate)} → {formatDateOnly(paymentTarget.endDate)} · <strong>{formatMoney(paymentTarget.value, paymentTarget.currency)}</strong> pendiente.
          </Alert>

          <Grid container spacing={1.2}>
            {resolvedPaymentConfig.mode === "erp" ? (
              <Grid item xs={12} md={7}>
                <TextField
                  select required fullWidth size="small" label="Caja o cuenta" value={paymentAction.cashRegisterId || ""}
                  onChange={changePaymentAction("cashRegisterId")}
                  helperText={!paymentAvailableRegisters.length ? "No hay una caja o cuenta disponible para esta moneda." : "Usa la misma integración de Gestión del negocio que el cobro durante la asignación."}
                >
                  {(resolvedPaymentConfig.registers || []).map((register) => {
                    const currencyMismatch = register.currency !== paymentTarget.currency;
                    const disabled = !register.available || currencyMismatch;
                    const reason = currencyMismatch ? `Moneda ${register.currency}` : register.unavailableReason;
                    return (
                      <MenuItem key={register.id} value={register.id} disabled={disabled}>
                        {register.name} · {PAYMENT_METHOD_LABELS[register.method] || register.method}{reason ? ` · ${reason}` : ""}
                      </MenuItem>
                    );
                  })}
                </TextField>
              </Grid>
            ) : (
              <Grid item xs={12} md={5}>
                <TextField select fullWidth size="small" label="Medio de pago" value={paymentAction.paymentMethod || "cash"} onChange={changePaymentAction("paymentMethod")}>
                  {(resolvedPaymentConfig.methods || Object.keys(PAYMENT_METHOD_LABELS)).map((method) => (
                    <MenuItem key={method} value={method}>{PAYMENT_METHOD_LABELS[method] || method}</MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
            <Grid item xs={12} md={resolvedPaymentConfig.mode === "erp" ? 5 : 7}>
              <TextField fullWidth size="small" label="Referencia del cobro (opcional)" value={paymentAction.paymentReference || ""}
                onChange={changePaymentAction("paymentReference")} inputProps={{ maxLength: 180 }} />
            </Grid>
            {resolvedPaymentConfig.mode === "administrative" ? (
              <Grid item xs={12}>
                <Alert severity="info">El cobro queda asentado administrativamente en esta membresía y no genera caja, movimiento ni asiento contable.</Alert>
              </Grid>
            ) : !paymentAvailableRegisters.length ? (
              <Grid item xs={12}>
                <Alert severity="warning">No hay una caja/cuenta disponible para {paymentTarget.currency}. Si requiere apertura, debe existir una sesión abierta y operable por tu usuario.</Alert>
              </Grid>
            ) : null}
          </Grid>

          <Button type="submit" variant="contained" sx={{ mt: 1.2 }} disabled={paymentSaving || !paymentActionReady}
            startIcon={paymentSaving ? <CircularProgress size={17} /> : <PaymentsRoundedIcon />}>
            {paymentSaving ? "Registrando una sola vez…" : "Confirmar cobro pendiente"}
          </Button>
        </Box>
      ) : null}

      {canManage && usageAction && usageTarget ? (
        <Box component="form" onSubmit={submitUsage} sx={{ mb: 1.4, p: 1.5, borderRadius: 2.5, border: "1px solid rgba(243,90,58,.2)", bgcolor: "rgba(243,90,58,.035)" }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="space-between" alignItems={{ sm: "center" }} sx={{ mb: 1 }}>
            <Box>
              <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>
                {usageAction.type === "reverse" ? "Anular consumo" : "Registrar uso"} · {usageTarget.item?.name || "Membresía"}
              </Typography>
              <Typography variant="caption" sx={{ color: "rgba(25,36,47,.55)" }}>
                {usageTarget.usage?.controlled
                  ? `Disponibles: ${usageTarget.usage.available} de ${usageTarget.usage.included} ${usageTarget.usage.label || "usos"}.`
                  : "Este período no controla usos."}
              </Typography>
            </Box>
            <Button size="small" onClick={() => setUsageAction(null)} disabled={usageSaving}>Cancelar</Button>
          </Stack>
          <Grid container spacing={1.2}>
            <Grid item xs={12} sm={4}>
              <TextField
                required
                fullWidth
                size="small"
                type="number"
                label={usageAction.type === "reverse" ? "Cantidad a reponer" : "Cantidad a consumir"}
                value={usageAction.quantity || ""}
                onChange={(event) => setUsageAction((current) => ({ ...current, quantity: event.target.value }))}
                inputProps={{ min: 1, max: usageAction.type === "consume" ? Number(usageTarget.usage?.available || 0) : undefined, step: 1 }}
                disabled={usageSaving || usageAction.type === "reverse"}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                size="small"
                label="Nota (opcional)"
                value={usageAction.note || ""}
                onChange={(event) => setUsageAction((current) => ({ ...current, note: event.target.value }))}
                inputProps={{ maxLength: 300 }}
                placeholder="Ej.: sesión presencial, acceso del día, corrección…"
                disabled={usageSaving}
              />
            </Grid>
            {usageAction.type === "reverse" ? (
              <Grid item xs={12}><Alert severity="warning">La corrección no borra el consumo original: genera un movimiento compensatorio y repone el cupo.</Alert></Grid>
            ) : null}
          </Grid>
          <Button type="submit" variant="contained" sx={{ mt: 1.2 }} disabled={usageSaving || !usageActionReady}
            startIcon={usageSaving ? <CircularProgress size={17} /> : usageAction.type === "reverse" ? <UndoRoundedIcon /> : <AddCircleOutlineRoundedIcon />}>
            {usageSaving ? "Guardando una sola vez…" : usageAction.type === "reverse" ? "Confirmar anulación" : "Confirmar uso"}
          </Button>
        </Box>
      ) : null}

      {loading ? (
        <DashboardLoadingState label="Cargando membresías del cliente…" minHeight={220} />
      ) : (
        <>
          {canManage && !newAssignmentItems.length ? (
            <Alert severity="info" sx={{ mb: 1.2 }}>
              No hay servicios o ítems habilitados para recurrencia. En Ítems, editá el servicio o producto que corresponda y activá “Puede usarse como membresía”. Los demás ítems siguen vendiéndose normalmente sin aparecer acá.
            </Alert>
          ) : null}
          {canManage && !pauseAction && !paymentAction && !usageAction ? (
            <Box component="form" onSubmit={submit} sx={{ p: 1.5, borderRadius: 2.5, bgcolor: "rgba(25,36,47,.025)" }}>
              {form.renewFromMembershipId ? (
                <Alert severity="info" sx={{ mb: 1.2 }} action={<Button size="small" onClick={() => setForm(emptyForm())}>Cancelar</Button>}>
                  <strong>Renovación breve:</strong> {renewalSource?.item?.name || "membresía"} termina operativamente {formatDateOnly(membershipEndDate(renewalSource))}.
                  {" "}El próximo inicio seguro es {formatDateOnly(renewalMinimumStart)}; podés cambiar de plan sin alterar el período anterior.
                </Alert>
              ) : null}
              <Grid container spacing={1.2}>
                <Grid item xs={12} md={7}>
                  <TextField select required fullWidth size="small" label={form.renewFromMembershipId ? "Servicio de la renovación" : "Servicio o ítem recurrente"} value={form.itemId} onChange={selectItem} disabled={!selectableItems.length || saving}
                    helperText={form.renewFromMembershipId ? "Podés mantener el servicio actual o elegir otro habilitado para esta renovación." : undefined}>
                    {selectableItems.map((item) => (
                      <MenuItem key={item.id} value={item.id}>
                        {item.name}{item.itemKind === "service" ? " · servicio" : " · ítem"}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={5}>
                  <TextField required fullWidth size="small" type="number" label="Valor" value={form.value}
                    onChange={change("value")} inputProps={{ min: 0, step: "0.01" }} disabled={saving}
                    helperText={selectedSuggestion ? "Propuesta del servicio; podés ajustarla para esta operación." : undefined} />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField required fullWidth size="small" type="date" label="Inicio" value={form.startDate}
                    onChange={change("startDate")} InputLabelProps={{ shrink: true }} disabled={saving}
                    inputProps={{ min: renewalMinimumStart || undefined }}
                    helperText={renewalSource ? `Mínimo seguro: ${formatDateOnly(renewalMinimumStart)}.` : undefined} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField select fullWidth size="small" label="Definir vigencia por" value={form.validityMode}
                    onChange={change("validityMode")} disabled={saving}>
                    <MenuItem value="period">Vigencia</MenuItem>
                    <MenuItem value="endDate">Vencimiento exacto</MenuItem>
                  </TextField>
                </Grid>
                {form.validityMode === "period" ? (
                  <>
                    <Grid item xs={5} sm={3} md={2}>
                      <TextField required fullWidth size="small" type="number" label="Vigencia" value={form.periodValue}
                        onChange={change("periodValue")} inputProps={{ min: 1, max: 10000, step: 1 }} disabled={saving} />
                    </Grid>
                    <Grid item xs={7} sm={3} md={3}>
                      <TextField select fullWidth size="small" label="Unidad" value={form.periodUnit}
                        onChange={change("periodUnit")} disabled={saving}>
                        <MenuItem value="days">Días</MenuItem><MenuItem value="weeks">Semanas</MenuItem>
                        <MenuItem value="months">Meses</MenuItem><MenuItem value="years">Años</MenuItem>
                      </TextField>
                    </Grid>
                  </>
                ) : (
                  <Grid item xs={12} sm={6} md={5}>
                    <TextField required fullWidth size="small" type="date" label="Vencimiento" value={form.endDate}
                      onChange={change("endDate")} InputLabelProps={{ shrink: true }} inputProps={{ min: form.startDate || undefined }}
                      disabled={saving} />
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Divider sx={{ my: .2 }} />
                  <FormControlLabel
                    control={<Checkbox checked={form.recordPayment} onChange={togglePayment} disabled={saving || !canCollect || !resolvedPaymentConfig.canRecord} />}
                    label={form.renewFromMembershipId ? "Registrar el cobro junto con la renovación" : "Registrar el cobro de esta membresía"}
                  />
                  {resolvedPaymentConfig.note ? (
                    <Typography variant="caption" display="block" sx={{ color: "rgba(25,36,47,.55)", mt: -.5 }}>
                      {resolvedPaymentConfig.note}
                    </Typography>
                  ) : null}
                </Grid>

                {form.recordPayment && resolvedPaymentConfig.mode === "erp" ? (
                  <>
                    <Grid item xs={12} md={7}>
                      <TextField
                        select required fullWidth size="small" label="Caja o cuenta" value={form.cashRegisterId}
                        onChange={change("cashRegisterId")} helperText={!availableRegisters.length ? "No hay una caja o cuenta disponible para esta moneda." : "El método de cobro se toma del tipo de caja o cuenta."}
                      >
                        {(resolvedPaymentConfig.registers || []).map((register) => {
                          const currencyMismatch = register.currency !== form.currency;
                          const disabled = !register.available || currencyMismatch;
                          const reason = currencyMismatch ? `Moneda ${register.currency}` : register.unavailableReason;
                          return (
                            <MenuItem key={register.id} value={register.id} disabled={disabled}>
                              {register.name} · {PAYMENT_METHOD_LABELS[register.method] || register.method}{reason ? ` · ${reason}` : ""}
                            </MenuItem>
                          );
                        })}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={5}>
                      <TextField fullWidth size="small" label="Referencia del cobro (opcional)" value={form.paymentReference}
                        onChange={change("paymentReference")} inputProps={{ maxLength: 180 }} />
                    </Grid>
                    {!resolvedPaymentConfig.canRecord ? (
                      <Grid item xs={12}><Alert severity="warning">Podés guardar la membresía sin cobro, pero tus permisos actuales no permiten registrar la cobranza en Gestión del negocio.</Alert></Grid>
                    ) : !availableRegisters.length ? (
                      <Grid item xs={12}><Alert severity="warning">No hay una caja/cuenta disponible para esta moneda y este usuario. Si requiere apertura, debe existir una sesión abierta a tu cargo. También podés desmarcar el cobro y guardar solamente el período.</Alert></Grid>
                    ) : null}
                  </>
                ) : null}

                {form.recordPayment && resolvedPaymentConfig.mode === "administrative" ? (
                  <>
                    <Grid item xs={12}><Alert severity="info">Este registro es administrativo: deja constancia del pago en la membresía y no genera caja, movimiento ni asiento contable.</Alert></Grid>
                    <Grid item xs={12} md={5}>
                      <TextField select fullWidth size="small" label="Medio de pago" value={form.paymentMethod} onChange={change("paymentMethod")}>
                        {(resolvedPaymentConfig.methods || Object.keys(PAYMENT_METHOD_LABELS)).map((method) => (
                          <MenuItem key={method} value={method}>{PAYMENT_METHOD_LABELS[method] || method}</MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={7}>
                      <TextField fullWidth size="small" label="Referencia del cobro (opcional)" value={form.paymentReference}
                        onChange={change("paymentReference")} inputProps={{ maxLength: 180 }} />
                    </Grid>
                  </>
                ) : null}
              </Grid>

              {form.itemId ? (
                <Box sx={{ mt: 1.4, p: 1.25, borderRadius: 2, border: "1px solid rgba(25,36,47,.1)", bgcolor: "#fff" }}>
                  <Typography variant="caption" sx={{ display: "block", fontWeight: 950, color: "rgba(25,36,47,.58)", mb: .35 }}>
                    Antes de confirmar
                  </Typography>
                  <Typography sx={{ fontWeight: 900, color: BRAND.dark }}>
                    {form.renewFromMembershipId ? "Renovación" : "Primera membresía"} · {selectedItem?.name || "Plan"}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "rgba(25,36,47,.66)", mt: .25 }}>
                    {formatDateOnly(form.startDate)} → {formatDateOnly(previewEndDate)} · {previewValidity}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "rgba(25,36,47,.66)" }}>
                    {previewAmountLabel} · {previewPayment}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "rgba(25,36,47,.66)" }}>
                    {previewUsage}
                  </Typography>
                  {form.renewFromMembershipId ? (
                    <Typography variant="caption" sx={{ display: "block", color: "rgba(25,36,47,.5)", mt: .35 }}>
                      {changingPlan ? `Cambio de plan: ${renewalSource?.item?.name || "anterior"} → ${selectedItem?.name || "nuevo"}. ` : ""}
                      El período anterior queda intacto y enlazado en el historial.
                    </Typography>
                  ) : null}
                  {!previewReady ? (
                    <Typography variant="caption" sx={{ display: "block", color: "warning.dark", fontWeight: 800, mt: .45 }}>
                      Completá una vigencia válida, respetá el inicio mínimo de la renovación y, si vas a cobrar, elegí una caja o cuenta disponible.
                    </Typography>
                  ) : null}
                </Box>
              ) : null}

              <Button type="submit" variant="contained" sx={{ mt: 1.2 }} disabled={saving || !items.length || !previewReady}
                startIcon={saving ? <CircularProgress size={17} /> : <CardMembershipRoundedIcon />}>
                {saving ? "Guardando una sola vez…" : form.renewFromMembershipId ? "Confirmar renovación" : "Confirmar primera membresía"}
              </Button>
              {!items.length ? <Typography variant="caption" sx={{ ml: 1.2, color: "warning.dark", fontWeight: 800 }}>Creá primero un ítem activo.</Typography> : null}
            </Box>
          ) : null}

          <Divider sx={{ my: 1.6 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 950, color: BRAND.dark, mb: .8 }}>Historial de períodos</Typography>
          {!memberships.length ? (
            <Typography variant="body2" sx={{ color: "rgba(25,36,47,.55)" }}>Todavía no tiene membresías.</Typography>
          ) : (
            <Stack spacing={.9}>
              {memberships.map((membership) => {
                const meta = statusMeta(membership.status);
                const payMeta = paymentMeta(membership.payment);
                const periodLabel = membership.period?.unit === "custom"
                  ? "vencimiento manual"
                  : `${membership.period?.value || 0} ${PERIOD_LABELS[membership.period?.unit] || membership.period?.unit}`;
                const paymentDetails = membership.payment?.recorded
                  ? [
                      PAYMENT_METHOD_LABELS[membership.payment.method] || membership.payment.method,
                      membership.payment.cashRegister?.name,
                      membership.payment.erp?.movementNumber ? `Mov. ${membership.payment.erp.movementNumber}` : "",
                      membership.payment.reference ? `Ref. ${membership.payment.reference}` : "",
                      formatDateTime(membership.payment.paidAt),
                    ].filter(Boolean).join(" · ")
                  : "";
                const operationalEndDate = membershipEndDate(membership);
                const hasFrozenExtension = Boolean(operationalEndDate && membership.endDate && operationalEndDate !== membership.endDate);
                const pauseHistory = membership.pause?.history || [];
                const auditHistory = [...(membership.history || [])].sort((a, b) => {
                  const aTime = a?.at ? new Date(a.at).getTime() : 0;
                  const bTime = b?.at ? new Date(b.at).getTime() : 0;
                  return bTime - aTime;
                });
                const reversedUsageIds = new Set((membership.usageMovements || [])
                  .filter((movement) => movement.type === "reversal" && movement.reversalOfMovementId)
                  .map((movement) => movement.reversalOfMovementId));
                const reversibleMovement = [...(membership.usageMovements || [])]
                  .reverse()
                  .find((movement) => movement.type === "consume" && !reversedUsageIds.has(movement.id)) || null;
                return (
                  <Box key={membership.id} sx={{ p: 1.2, borderRadius: 2, bgcolor: "rgba(25,36,47,.035)" }}>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="space-between" alignItems={{ sm: "center" }}>
                      <Box>
                        <Stack direction="row" spacing={.7} alignItems="center" flexWrap="wrap">
                          <Typography sx={{ fontWeight: 900, color: BRAND.dark }}>{membership.item?.name || "Membresía"}</Typography>
                          <Chip size="small" color={meta.color} label={meta.label} sx={{ fontWeight: 800 }} />
                          {membership.renewedFromMembershipId ? <Chip size="small" variant="outlined" label="Renovación" /> : null}
                          {membership.renewedToMembershipId ? <Chip size="small" variant="outlined" label="Continuada" /> : null}
                          {payMeta ? <Chip size="small" color={payMeta.color} variant="outlined" label={payMeta.label} /> : null}
                        </Stack>
                        <Typography variant="body2" sx={{ color: "rgba(25,36,47,.62)" }}>
                          {formatDateOnly(membership.startDate)} → {formatDateOnly(membership.endDate)} · {periodLabel} · {formatMoney(membership.value, membership.currency)}
                        </Typography>
                        {membership.usage?.controlled ? (
                          <Stack direction="row" spacing={.7} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: .35 }}>
                            <Typography variant="caption" sx={{ color: "rgba(25,36,47,.62)", fontWeight: 850 }}>
                              Uso: {membership.usage.consumed}/{membership.usage.included} {membership.usage.label || "usos"} · {membership.usage.available} disponibles
                            </Typography>
                            {membership.usage.exhausted ? <Chip size="small" color="warning" label="Agotada" sx={{ fontWeight: 850 }} /> : null}
                          </Stack>
                        ) : (
                          <Typography variant="caption" display="block" sx={{ color: "rgba(25,36,47,.48)" }}>Sin límite de usos</Typography>
                        )}
                        {hasFrozenExtension || membership.status === "paused" ? (
                          <Typography variant="caption" display="block" sx={{ color: membership.status === "paused" ? "warning.dark" : "rgba(25,36,47,.56)", fontWeight: 800 }}>
                            Vencimiento original {formatDateOnly(membership.endDate)} · operativo {formatDateOnly(operationalEndDate)}
                            {membership.pause?.totalFrozenDays ? ` · ${membership.pause.totalFrozenDays} días congelados` : ""}
                          </Typography>
                        ) : null}
                        {membership.pause?.current ? (
                          <Typography variant="caption" display="block" sx={{ color: "warning.dark", fontWeight: 800 }}>
                            Pausa abierta desde {formatDateOnly(membership.pause.current.startDate)} · {membership.pause.current.reason || "Sin motivo"}
                          </Typography>
                        ) : null}
                        {pauseHistory.length ? (
                          <Box sx={{ mt: .45, pl: .8, borderLeft: "2px solid rgba(25,36,47,.08)" }}>
                            {pauseHistory.map((pause) => (
                              <Typography key={pause.id || `${pause.startDate}-${pause.resumedDate || "open"}`} variant="caption" display="block" sx={{ color: "rgba(25,36,47,.5)" }}>
                                Pausa {formatDateOnly(pause.startDate)} → {pause.resumedDate ? formatDateOnly(pause.resumedDate) : "en curso"}
                                {Number.isFinite(Number(pause.frozenDays)) ? ` · ${Number(pause.frozenDays)} días` : ""}
                                {pause.reason ? ` · ${pause.reason}` : ""}
                                {pause.startedBy?.name || pause.startedBy?.email ? ` · inició ${pause.startedBy?.name || pause.startedBy?.email}` : ""}
                                {pause.resumedDate && (pause.resumedBy?.name || pause.resumedBy?.email) ? ` · reanudó ${pause.resumedBy?.name || pause.resumedBy?.email}` : ""}
                                {pause.resumeNote ? ` · ${pause.resumeNote}` : ""}
                              </Typography>
                            ))}
                          </Box>
                        ) : null}
                        {paymentDetails ? (
                          <Typography variant="caption" display="block" sx={{ color: "rgba(25,36,47,.52)" }}>
                            Cobro: {formatMoney(membership.payment.amount, membership.payment.currency)} · {paymentDetails}
                          </Typography>
                        ) : null}
                        {membership.payment?.online?.reviewRequired ? (
                          <Alert severity="warning" sx={{ mt: .6, py: .2 }}>
                            Mercado Pago informó un cobro que requiere conciliación manual. {membership.payment.online.error || "Revisá la operación antes de registrar otro cobro."}
                          </Alert>
                        ) : null}
                        {membership.cancellation ? (
                          <Typography variant="caption" display="block" sx={{ color: "error.main", mt: .25 }}>
                            Cancelada el {formatDateTime(membership.cancellation.cancelledAt)} por {membership.cancellation.cancelledBy?.name || membership.cancellation.cancelledBy?.email || "Responsable no registrado"}. Motivo: {membership.cancellation.reason}
                          </Typography>
                        ) : null}
                        {auditHistory.length ? (
                          <Box sx={{ mt: .75, p: .85, borderRadius: 1.5, bgcolor: "rgba(255,255,255,.62)", border: "1px solid rgba(25,36,47,.07)" }}>
                            <Typography variant="caption" display="block" sx={{ color: "rgba(25,36,47,.5)", fontWeight: 900, mb: .35 }}>
                              Auditoría · solo lectura
                            </Typography>
                            <Stack spacing={.35}>
                              {auditHistory.map((event, index) => {
                                const details = auditEventDetails(event);
                                return (
                                  <Typography
                                    key={`${event.type || "event"}-${event.at || index}-${index}`}
                                    variant="caption"
                                    display="block"
                                    sx={{ color: "rgba(25,36,47,.58)", overflowWrap: "anywhere" }}
                                  >
                                    <Box component="span" sx={{ fontWeight: 900, color: BRAND.dark }}>{auditEventLabel(event.type)}</Box>
                                    {event.at ? ` · ${formatDateTime(event.at)}` : ""}
                                    {` · ${auditActorLabel(event.actor)}`}
                                    {details ? ` · ${details}` : ""}
                                  </Typography>
                                );
                              })}
                            </Stack>
                          </Box>
                        ) : (
                          <Typography variant="caption" sx={{ color: "rgba(25,36,47,.48)" }}>
                            Auditoría anterior sin responsable detallado.
                          </Typography>
                        )}
                      </Box>
                      <Stack direction="row" spacing={.7} flexWrap="wrap" useFlexGap justifyContent={{ sm: "flex-end" }}>
                        {canManage && membership.status === "active" && membership.usage?.controlled ? (
                          membership.usage.exhausted ? (
                            <Chip size="small" color="warning" variant="outlined" label="Cupo agotado" />
                          ) : (
                            <Button size="small" variant="contained" startIcon={<AddCircleOutlineRoundedIcon />} onClick={() => beginUsage(membership)}>Registrar uso</Button>
                          )
                        ) : null}
                        {canManage && reversibleMovement ? (
                          <Button size="small" variant="text" startIcon={<UndoRoundedIcon />} onClick={() => beginUsageReversal(membership, reversibleMovement)}>Anular último uso</Button>
                        ) : null}
                        {canCollect && !membership.payment?.recorded && Number(membership.value) > 0 ? (
                          resolvedPaymentConfig.canRecord ? (
                            <Button
                              size="small"
                              variant="contained"
                              startIcon={<PaymentsRoundedIcon />}
                              onClick={() => beginPayment(membership)}
                              disabled={paymentSaving}
                            >
                              Registrar cobro
                            </Button>
                          ) : (
                            <Chip size="small" color="warning" variant="outlined" label="Cobro requiere permisos" />
                          )
                        ) : null}
                        {resolvedPaymentConfig.canReadMovements && membership.payment?.erp?.movementId ? (
                          <Button
                            size="small"
                            variant="text"
                            component={RouterLink}
                            to={`/dashboard/proyectos/${projectId}/sistema/movimientos?movementId=${encodeURIComponent(membership.payment.erp.movementId)}`}
                            startIcon={<OpenInNewRoundedIcon />}
                          >
                            Ver movimiento
                          </Button>
                        ) : null}
                        {canManage && membership.status === "paused" ? (
                          <Button size="small" variant="contained" startIcon={<PlayCircleOutlineRoundedIcon />} onClick={() => beginResume(membership)}>Reanudar</Button>
                        ) : canManage && membership.status === "active" && !membership.renewedToMembershipId ? (
                          <Button size="small" variant="outlined" startIcon={<PauseCircleOutlineRoundedIcon />} onClick={() => beginPause(membership)}>Pausar</Button>
                        ) : null}
                        {canManage && !['scheduled', 'paused', 'cancelled'].includes(membership.status) && !membership.renewedToMembershipId ? (
                          <Button size="small" variant="outlined" startIcon={<AutorenewRoundedIcon />} onClick={() => beginRenewal(membership)}>Renovar</Button>
                        ) : null}
                        {canManage && ["active", "scheduled"].includes(membership.status) ? (
                          <Button size="small" color="error" variant="outlined" startIcon={<CancelRoundedIcon />} onClick={() => beginCancellation(membership)}>Cancelar</Button>
                        ) : null}
                        {membership.renewedToMembershipId ? (
                          <Typography variant="caption" sx={{ color: "rgba(25,36,47,.5)", fontWeight: 800, alignSelf: "center" }}>Ya continuada</Typography>
                        ) : null}
                      </Stack>
                    </Stack>
                    {canManage && cancelMembershipId === membership.id ? (
                      <Box sx={{ mt: 1, p: 1.1, borderRadius: 2, bgcolor: "rgba(211,47,47,.045)" }}>
                        <Alert severity="warning" sx={{ mb: 1 }}>
                          La membresía dejará de figurar como vigente, pero el período y su historial se conservan. Esta acción no devuelve ni revierte cobros: cualquier ajuste económico debe hacerse desde los mecanismos financieros existentes.
                        </Alert>
                        <TextField
                          fullWidth required size="small" label="Motivo de cancelación" value={cancelReason}
                          onChange={(event) => setCancelReason(event.target.value)} inputProps={{ maxLength: 600 }}
                          helperText="El motivo, la fecha y el usuario quedarán registrados en el historial."
                        />
                        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                          <Button
                            size="small" color="error" variant="contained" disabled={cancelling || !cancelReason.trim()}
                            onClick={() => cancelMembership(membership)}
                            startIcon={cancelling ? <CircularProgress size={16} /> : <CancelRoundedIcon />}
                          >
                            Confirmar cancelación
                          </Button>
                          <Button size="small" disabled={cancelling} onClick={() => { setCancelMembershipId(""); setCancelReason(""); }}>
                            Volver
                          </Button>
                        </Stack>
                      </Box>
                    ) : null}
                  </Box>
                );
              })}
            </Stack>
          )}
        </>
      )}
    </Paper>
  );
}
